import * as THREE from 'three';

export function createOpticalGrade() {
  return {
    uniforms: {
      tDiffuse: { value: null },
      uTime: { value: 0 },
      uProgress: { value: 0 },
      uWarp: { value: 0 },
      uAspect: { value: innerWidth / innerHeight },
      uFade: { value: 0 },
      uTexel: { value: new THREE.Vector2(1 / innerWidth, 1 / innerHeight) },
      uDeterministicBloom: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;

      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      precision highp float;

      uniform sampler2D tDiffuse;
      uniform float uTime;
      uniform float uProgress;
      uniform float uWarp;
      uniform float uFade;
      uniform float uAspect;
      uniform vec2 uTexel;
      uniform float uDeterministicBloom;
      varying vec2 vUv;

      float hash(vec2 p) {
        return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
      }

      float ss(float a, float b, float x) {
        return smoothstep(a, b, x);
      }

      vec3 aces(vec3 x) {
        const float a = 2.51;
        const float b = 0.03;
        const float c = 2.43;
        const float d = 0.59;
        const float e = 0.14;
        return clamp((x * (a * x + b)) / (x * (c * x + d) + e), 0.0, 1.0);
      }

      vec3 brightSample(vec2 uv, float threshold) {
        vec3 value = texture2D(tDiffuse, clamp(uv, 0.0, 1.0)).rgb;
        float peak = max(max(value.r, value.g), value.b);
        float gate = ss(threshold, threshold + 0.34, peak);
        return max(value - threshold * 0.55, 0.0) * gate;
      }

      void main() {
        vec2 uv = vUv;
        vec2 center = uv - 0.5;
        float r = length(vec2(center.x * uAspect * 0.68, center.y));

        // Lens-edge separation stays sub-pixel through most of the image and
        // only becomes readable at high-contrast outer edges.
        float ca = (0.00055 + uWarp * 0.0021) * ss(0.18, 0.94, r);
        vec3 col;
        col.r = texture2D(tDiffuse, uv - center * ca).r;
        col.g = texture2D(tDiffuse, uv).g;
        col.b = texture2D(tDiffuse, uv + center * ca).b;

        // Frozen captures cannot rely on the renderer's mip chain across all
        // software WebGL implementations. This fixed multi-radius kernel is
        // deterministic but still supplies a proper photographic halation.
        if (uDeterministicBloom > 0.001) {
          vec3 nearGlow = vec3(0.0);
          nearGlow += brightSample(uv + vec2( 2.0,  0.0) * uTexel, 0.42);
          nearGlow += brightSample(uv + vec2(-2.0,  0.0) * uTexel, 0.42);
          nearGlow += brightSample(uv + vec2( 0.0,  2.0) * uTexel, 0.42);
          nearGlow += brightSample(uv + vec2( 0.0, -2.0) * uTexel, 0.42);

          vec3 middleGlow = vec3(0.0);
          middleGlow += brightSample(uv + vec2( 5.0,  4.0) * uTexel, 0.50);
          middleGlow += brightSample(uv + vec2(-5.0,  4.0) * uTexel, 0.50);
          middleGlow += brightSample(uv + vec2( 5.0, -4.0) * uTexel, 0.50);
          middleGlow += brightSample(uv + vec2(-5.0, -4.0) * uTexel, 0.50);

          vec3 farGlow = vec3(0.0);
          farGlow += brightSample(uv + vec2( 10.0,  0.0) * uTexel, 0.62);
          farGlow += brightSample(uv + vec2(-10.0,  0.0) * uTexel, 0.62);
          farGlow += brightSample(uv + vec2( 0.0,  9.0) * uTexel, 0.62);
          farGlow += brightSample(uv + vec2( 0.0, -9.0) * uTexel, 0.62);

          vec3 glow = nearGlow * 0.070 + middleGlow * 0.048 + farGlow * 0.032;
          col += glow * uDeterministicBloom;
          col += glow * vec3(1.08, 0.52, 0.34) * 0.035 * uDeterministicBloom;
        }

        float lum = dot(col, vec3(0.2126, 0.7152, 0.0722));
        float current = ss(0.70, 0.77, uProgress) * (1.0 - ss(0.85, 0.91, uProgress));
        float shore = ss(0.86, 0.95, uProgress);

        // Indigo-violet shadows and pearl highlights, with only the final act
        // allowed a restrained rose bias.
        vec3 cool = col * vec3(0.88, 0.95, 1.10);
        vec3 pearl = col * vec3(1.035, 1.015, 0.965);
        col = mix(cool, pearl, ss(0.045, 0.82, lum));
        col *= mix(vec3(1.0), vec3(0.93, 1.035, 1.06), current * 0.45);
        col *= mix(vec3(1.0), vec3(1.045, 0.94, 1.015), shore * 0.42);

        float exposure = 1.02 + current * 0.06 + shore * 0.025 + uWarp * 0.045;
        col = aces(col * exposure);

        // Fine optical finish: a soft canopy vignette and luminance-aware
        // grain that never turns the near-black sky into crawling noise.
        col *= 1.0 - ss(0.48, 1.03, r) * 0.34;
        float grain = hash(gl_FragCoord.xy + fract(uTime) * 91.7) - 0.5;
        float grainAmount = (0.009 + uWarp * 0.004) * (0.32 + ss(0.01, 0.30, lum) * 0.68);
        col += grain * grainAmount;

        col = mix(col, vec3(0.0), uFade);
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  };
}
